import React, { useEffect } from 'react';
import {
  TextField,
  Box,
  Typography,
  Alert,
  FormHelperText,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

interface BasicInfoProps {
  data: any;
  onChange: (data: any) => void;
  onComplete: (completed: boolean) => void;
}

// Convert DD-MM-YYYY string to Date object
const parseDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Months are 0-indexed
    const year = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }
  return null;
};

// Convert Date object to DD-MM-YYYY string in IST
const formatDateToIST = (date: Date | null): string => {
  if (!date) return '';
  // Convert to IST (UTC+5:30)
  const istDate = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const day = String(istDate.getDate()).padStart(2, '0');
  const month = String(istDate.getMonth() + 1).padStart(2, '0');
  const year = istDate.getFullYear();
  return `${day}-${month}-${year}`;
};

const BasicInfo: React.FC<BasicInfoProps> = ({ data, onChange, onComplete }) => {
  const handleChange = (field: string, value: any) => {
    const newData = { ...data, [field]: value };
    onChange(newData);

    // Check if section is complete
    const isComplete = !!(
      newData.title &&
      newData.title.length >= 10 &&
      newData.dateOfConception &&
      newData.stageOfDevelopment &&
      newData.summary &&
      newData.summary.length >= 50
    );
    onComplete(isComplete);
  };

  useEffect(() => {
    // Check completion on mount
    const isComplete = !!(
      data.title &&
      data.title.length >= 10 &&
      data.dateOfConception &&
      data.stageOfDevelopment &&
      data.summary &&
      data.summary.length >= 50
    );
    onComplete(isComplete);
  }, []);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Basic Information
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Please provide the basic information about your invention. All fields marked with * are required.
        </Typography>

        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Guidelines for Patent-Ready Description:
        </Typography>

        <Typography variant="body2" component="div" sx={{ mb: 1 }}>
          <strong>Title:</strong> Use a specific, descriptive title that clearly indicates what the invention is – not generic marketing language. Good: "Thermal Management System for Lithium-Ion Batteries." Avoid: "Revolutionary Battery Innovation."
        </Typography>

        <Typography variant="body2" component="div" sx={{ mb: 1 }}>
          <strong>Date of Conception:</strong> Record the earliest date you can prove the invention was conceived. Maintain documentation (emails, lab notebooks, sketches, dated prototypes) as evidence. This date is legally important for establishing priority.
        </Typography>

        <Typography variant="body2" component="div" sx={{ mb: 1 }}>
          <strong>Agreements/Contracts:</strong> Disclose any agreements, sponsored research, or employment contracts that may affect ownership rights. This information is critical for determining who can file the patent and avoiding legal complications.
        </Typography>

        <Typography variant="body2">
          <strong>Summary:</strong> Provide a clear technical summary focused on what the invention does and the problem it solves. Avoid marketing language or exaggerated claims. Think: "technical abstract" not "sales pitch."
        </Typography>
      </Alert>

      <TextField
        fullWidth
        required
        label="Title of Invention"
        value={data.title || ''}
        onChange={(e) => handleChange('title', e.target.value)}
        margin="normal"
        error={data.title && data.title.length < 10}
        helperText={
          data.title && data.title.length < 10
            ? `Title must be at least 10 characters (currently ${data.title.length})`
            : "Provide a clear, descriptive title for your invention (recommended length 10 to 15 words)"
        }
        inputProps={{ maxLength: 200 }}
      />

      <Box sx={{ mt: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Date of Conception *"
            value={data.dateOfConception ? parseDate(data.dateOfConception) : null}
            onChange={(newValue) => handleChange('dateOfConception', formatDateToIST(newValue))}
            format="dd-MM-yyyy"
            slotProps={{
              textField: {
                fullWidth: true,
                helperText: "When did you first conceive this invention? (DD-MM-YYYY)",
              },
            }}
            maxDate={new Date()}
          />
        </LocalizationProvider>
      </Box>

      <TextField
        fullWidth
        label="Field/Domain of Invention"
        value={data.field || ''}
        onChange={(e) => handleChange('field', e.target.value)}
        margin="normal"
        helperText="e.g., Software, Electronics, Mechanical, Biotechnology, etc. or write a full sentence: 'This invention relates to battery thermal management systems in electric vehicles.'"
        placeholder="Select or enter the field"
      />

      {/* New Question 1: Agreement/Contract/MoU */}
      <Box sx={{ mt: 3 }}>
        <FormControl component="fieldset">
          <FormLabel component="legend">
            Is the work bound by any agreement / contract / MoU? *
          </FormLabel>
          <RadioGroup
            value={data.boundByAgreement || ''}
            onChange={(e) => handleChange('boundByAgreement', e.target.value)}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>

        {data.boundByAgreement === 'yes' && (
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Details of Organisation and Nature of Collaboration"
            value={data.agreementDetails || ''}
            onChange={(e) => handleChange('agreementDetails', e.target.value)}
            margin="normal"
            required
            helperText="Please provide full details including organization name and nature of collaboration"
          />
        )}
      </Box>

      {/* New Question 2: Stage of Development */}
      <Box sx={{ mt: 3 }}>
        <TextField
          fullWidth
          required
          select
          label="Stage of Development"
          value={data.stageOfDevelopment || ''}
          onChange={(e) => handleChange('stageOfDevelopment', e.target.value)}
          margin="normal"
          helperText="Select the most applicable stage your invention currently lies in"
        >
          <MenuItem value="idea">Idea / Concept</MenuItem>
          <MenuItem value="early-stage">Early Stage</MenuItem>
          <MenuItem value="proof-of-concept">Proof of Concept</MenuItem>
          <MenuItem value="prototype">Prototype</MenuItem>
          <MenuItem value="industry-ready">Industry Interest / Ready to Use</MenuItem>
        </TextField>
      </Box>

      {/* Brief Summary - moved to end */}
      <TextField
        fullWidth
        required
        multiline
        rows={4}
        label="Brief Summary"
        value={data.summary || ''}
        onChange={(e) => handleChange('summary', e.target.value)}
        margin="normal"
        error={data.summary && data.summary.length < 50}
        helperText={
          data.summary && data.summary.length < 50
            ? `Summary must be at least 50 characters (currently ${data.summary.length})`
            : "Give a high-level summary: what the invention does and how it solves a problem. State the purpose or technical advantage, but don't make marketing claims."
        }
        inputProps={{ maxLength: 2000 }}
      />

    </Box>
  );
};

export default BasicInfo;